import { Box } from "@mui/material";
import type { PresenceStatus } from "@xop/dashboard-shared";

/** The online statuses, plus an explicit `offline` state for rosters. */
export type PresenceState = PresenceStatus | "offline";

/**
 * A small filled circle showing presence at a glance:
 *   active  = VIVID bright green (`palette.presence.online`)
 *   away    = amber
 *   dnd     = red
 *   offline = muted light grey (`palette.presence.offline`)
 * Online vs offline are deliberately far apart in hue + brightness so they're
 * unmistakable under any lighting. `ring` draws a paper-coloured border so the
 * dot reads when overlaid on an avatar.
 *
 * Shared by the profile menu (the caller's own status) and the chat roster
 * (every teammate's presence) so the indicator looks identical everywhere.
 */
export function PresenceDot({
  state,
  size = 9,
  ring = false,
}: {
  state: PresenceState;
  size?: number;
  ring?: boolean;
}): JSX.Element {
  return (
    <Box
      component="span"
      aria-hidden
      sx={{
        display: "inline-block",
        height: size,
        width: size,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: (t) => {
          if (state === "offline") return t.palette.presence.offline;
          if (state === "active") return t.palette.presence.online;
          return state === "away" ? t.palette.warning.main : t.palette.error.main;
        },
        ...(ring ? { border: 2, borderColor: "background.paper" } : {}),
      }}
    />
  );
}
